// My Dashboard modular layout — order, width (full/half) and height per box, saved to the account with named views.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const DEFAULT_LAYOUT = [
  { id: "donext", w: "full", h: "auto" },
  { id: "callweek", w: "half", h: "auto" },
  { id: "mytasks", w: "half", h: "auto" },
  { id: "pool", w: "half", h: "auto" },
  { id: "checkins", w: "half", h: "auto" },
  { id: "notepad", w: "half", h: "auto" },
  { id: "customers", w: "half", h: "m" },
  { id: "activities", w: "half", h: "m" },
  { id: "quotes", w: "half", h: "auto" },
  { id: "outbound", w: "half", h: "auto" },
  { id: "signups", w: "half", h: "auto" },
  { id: "dev", w: "half", h: "auto" },
];
export const HEIGHTS = { auto: "Fit", s: "Short", m: "Medium", l: "Tall" };
export const HEIGHT_PX = { s: 360, m: 620, l: 920 };

// Boxes are never removed: unknown ids drop, missing ones are appended from the default.
export const normalize = (layout) => {
  const known = new Set(DEFAULT_LAYOUT.map((m) => m.id));
  const base = (Array.isArray(layout) ? layout : []).filter((m) => known.has(m.id));
  const have = new Set(base.map((m) => m.id));
  return [...base, ...DEFAULT_LAYOUT.filter((m) => !have.has(m.id))];
};

// Rule-based tidy (no AI): boxes with content keep their order at the top, empty ones sink; heights follow row counts.
export const tidyLayout = (layout, counts) => {
  const h = (n) => (n <= 0 ? "auto" : n <= 6 ? "auto" : n <= 15 ? "m" : "l");
  const rows = layout.map((m, i) => ({ ...m, i, n: counts[m.id] ?? 0 }));
  const keep = (m) => m.id === "notepad" || m.n > 0;
  const sorted = [...rows.filter(keep), ...rows.filter((m) => !keep(m))];
  return sorted.map(({ i, n, ...m }) => ({ ...m, h: m.id === "notepad" ? m.h : h(n) }));
};
const weekStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };

export function useDashLayout() {
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [views, setViews] = useState([]);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState({ locked: false, tidiedAt: null, loaded: false });
  const ready = useRef(false);
  useEffect(() => {
    axios.get(`${API}/me/dashboard-layout`, { withCredentials: true }).then(({ data }) => {
      setLayout(normalize(data.current)); setViews(data.views || []); setActive(data.active_view || null);
      setMeta({ locked: !!data.locked, tidiedAt: data.tidied_at || null, loaded: true });
    }).catch(() => {}).finally(() => { ready.current = true; });
  }, []);
  const save = useCallback((body) => axios.put(`${API}/me/dashboard-layout`, body, { withCredentials: true }).catch(() => {}), []);
  const update = useCallback((next) => { setLayout(next); setActive(null); if (ready.current) save({ current: next, active_view: null }); }, [save]);
  const move = (id, to) => {
    const from = layout.findIndex((m) => m.id === id);
    if (from < 0 || to < 0 || to >= layout.length || from === to) return;
    const n = [...layout]; const [m] = n.splice(from, 1); n.splice(to, 0, m); update(n);
  };
  const patch = (id, p) => update(layout.map((m) => (m.id === id ? { ...m, ...p } : m)));
  const applyView = (name) => {
    const v = views.find((x) => x.name === name); if (!v) return;
    const next = normalize(v.layout); setLayout(next); setActive(name); save({ current: next, active_view: name });
  };
  const saveView = (name) => {
    const nv = [...views.filter((v) => v.name !== name), { name, layout }];
    setViews(nv); setActive(name); save({ views: nv, current: layout, active_view: name });
  };
  const deleteView = (name) => {
    const nv = views.filter((v) => v.name !== name);
    setViews(nv); if (active === name) setActive(null); save({ views: nv, active_view: active === name ? null : active });
  };
  const reset = () => { setLayout(DEFAULT_LAYOUT); setActive(null); save({ current: null, active_view: null }); };
  const optimise = async (counts, sales) => {
    const { data } = await axios.post(`${API}/me/dashboard-layout/optimise`, { counts, sales }, { withCredentials: true });
    const got = new Set(data.layout.map((m) => m.id));
    setPreview(normalize([...data.layout, ...layout.filter((m) => !got.has(m.id))]));
    return data;
  };
  const keepPreview = () => { if (preview) update(preview); setPreview(null); };
  const cancelPreview = () => setPreview(null);
  const setLocked = (locked) => { setMeta((m) => ({ ...m, locked })); save({ locked }); };
  // Weekly auto-tidy: once per week (Monday start) unless the user locked their layout. Returns the previous layout for Undo.
  const weeklyTidy = (counts) => {
    if (!meta.loaded || meta.locked || preview) return null;
    if (meta.tidiedAt && new Date(meta.tidiedAt) >= weekStart()) return null;
    const prev = layout;
    const next = tidyLayout(layout, counts);
    setMeta((m) => ({ ...m, tidiedAt: new Date().toISOString() }));
    setLayout(next); setActive(null); save({ current: next, active_view: null, tidied: true });
    return JSON.stringify(next) === JSON.stringify(prev) ? null : prev;
  };
  const undoTidy = (prev) => { setLayout(prev); save({ current: prev, active_view: null }); };
  return { layout: preview || layout, views, active, move, patch, applyView, saveView, deleteView, reset, optimise, keepPreview, cancelPreview, previewing: !!preview,
    locked: meta.locked, metaLoaded: meta.loaded, setLocked, weeklyTidy, undoTidy };
}
